import { useState, useEffect } from "react";
import axios from "axios";
import { useLocation, Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

// @mui material components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
// Icons
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PersonIcon from "@mui/icons-material/Person";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Breadcrumbs from "examples/Breadcrumbs";

// Redux & Context
import { useSelector } from "react-redux";
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

// Custom styles
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, darkMode } = controller;

  // States for Profile Menu
  const [anchorEl, setAnchorEl] = useState(null);

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const route = pathname.split("/").slice(1);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const account = useSelector((state) => state.account.data?.accountModel?.account);
  const fullName = account ? `${account.firstName || ""} ${account.lastName || ""}`.trim() : "User";

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);

  // Profile Menu Handlers
  const handleOpenProfileMenu = (event) => setAnchorEl(event.currentTarget);
  const handleCloseProfileMenu = () => setAnchorEl(null);

  const handleLogout = async () => {
    const token = localStorage.getItem("token");
    try {
      if (token) {
        await axios.get("https://v3.dhundhoo.com/auth/signout", {
          headers: { "x-dhundhoo-session": token },
        });
      }
    } catch (error) {
      console.error("Logout failed", error);
    } finally {
      localStorage.removeItem("token");
      navigate("/authentication/sign-in");
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    handleCloseProfileMenu();
  };

  // Render the Profile Dropdown
  const renderProfileMenu = () => (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleCloseProfileMenu}
      sx={{ mt: "45px" }}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <MDBox px={2} py={1}>
        <MDTypography variant="button" fontWeight="bold">
          {fullName}
        </MDTypography>
      </MDBox>
      <Divider sx={{ my: 0.5 }} />
      <MenuItem onClick={() => handleNavigate("/profile")}>
        <PersonIcon sx={{ mr: 1 }} fontSize="small" /> Profile
      </MenuItem>
      {/* <MenuItem onClick={() => handleNavigate("/help")}>
        <HelpOutlineIcon sx={{ mr: 1 }} fontSize="small" /> Help & Feedback
      </MenuItem> */}
      <Divider sx={{ my: 0.5 }} />
      <MenuItem onClick={handleLogout} sx={{ color: "error.main" }}>
        <LogoutIcon sx={{ mr: 1 }} fontSize="small" /> Logout
      </MenuItem>
    </Menu>
  );

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => ({
        ...navbar(theme, { transparentNavbar, absolute, light, darkMode }),
        marginTop: "-5px !important",
        paddingTop: "-5px !important",
        paddingBottom: "0px !important",
        marginBottom: "0px !important",
      })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </MDBox>

        {isMini ? null : (
          <MDBox sx={(theme) => ({
            ...navbarRow(theme, { isMini }),
            marginLeft: "auto",
            display: "flex",
            alignItems: "center"
          })}>

            {/* Mobile Menu Icon */}
            <IconButton
              size="small"
              disableRipple
              color="inherit"
              sx={navbarMobileMenu}
              onClick={handleMiniSidenav}
            >
              <Icon fontSize="medium">{miniSidenav ? "menu_open" : "menu"}</Icon>
            </IconButton>

            {/* Recent Alerts */}
            <IconButton
              size="medium"
              color="inherit"
              onClick={() => navigate("/Alerts/Recent-Alerts")}
              sx={{ ml: 1, mt: "-3", pb: 1 }}
            >
              <NotificationsNoneIcon fontSize="medium" />
            </IconButton>

            {/* Profile Icon Button */}
            <IconButton
              size="medium"
              color="inherit"
              onClick={handleOpenProfileMenu}
              sx={{ ml: 1, mt: "-3", pb: 1 }}
            >
              <AccountCircleIcon fontSize="medium" />
            </IconButton>

            {renderProfileMenu()}
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = { absolute: false, light: false, isMini: false };
DashboardNavbar.propTypes = { absolute: PropTypes.bool, light: PropTypes.bool, isMini: PropTypes.bool };

export default DashboardNavbar;